import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, FileText, Loader2, ShieldCheck, Sparkles, AlertTriangle, Lock, Clock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import axios from "axios";

const CATEGORY_LABELS = {
  cuidados_pessoais: "Cuidados pessoais",
  manutencao_espaco: "Manutenção do espaço",
  roupa: "Roupa",
  refeicoes: "Refeições",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicCarePlan — client-facing page for `/care-plan/:token`.
 *
 * Shows the objective of the service + daily activities the AFs will develop.
 * Marked PROVISÓRIO until the TSC validates on-site. Uses the Caring blue
 * palette (no dark backgrounds) to feel warm and clinical without being cold.
 * Static texts come from PublicCopy so they can be edited in
 * `/settings/public-copy` without touching code.
 */
export default function PublicCarePlan() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [copy, setCopy] = useState({});

  useEffect(() => {
    api.get(`/public/care-plan/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Não foi possível carregar o Plano de Cuidados."));
    axios.get(`${BACKEND_URL}/api/public-copy`).then((r) => setCopy(r.data || {})).catch(() => {});
  }, [token]);

  if (err) return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="care-plan-error">
      <div className="max-w-lg text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--warning))]" strokeWidth={1.5} />
        <div className="shift-chip shift-chip-warn mb-6 mx-auto">Plano indisponível</div>
        <h1 className="font-serif text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">Plano não disponível</h1>
        <p className="text-muted-foreground text-lg leading-relaxed">{err}</p>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="care-plan-loading">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <h1 className="font-serif text-2xl font-bold tracking-tight text-foreground">A carregar…</h1>
      </div>
    </div>
  );

  // Contract not yet validated by the manager — show a warm waiting state.
  if (data.locked) {
    const step = data.accepted ? (data.contract_completed ? 2 : 1) : 0;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="care-plan-locked">
        <div className="bg-card border border-border rounded-3xl p-10 max-w-xl w-full shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-[hsl(var(--warning))]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold">{copy.cp_locked_kicker || "Plano de cuidados"}</div>
              <div className="font-display text-2xl text-primary">{copy.cp_locked_title || "A preparar o seu plano."}</div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            {copy.cp_locked_body || "O Plano de Cuidados individual estará disponível assim que a sua Gestora validar os dados do contrato. Recebemos a informação necessária e estamos a preparar tudo para o arranque."}
          </p>
          <div className="space-y-3 mb-6" data-testid="care-plan-status-steps">
            <StatusStep done={data.accepted} label="Proposta aceite" testId="step-accepted" />
            <StatusStep done={data.contract_completed} label="Dados do contrato preenchidos" testId="step-contract-data" />
            <StatusStep done={false} label="Validação pela Gestora → liberta o Plano de Cuidados" pending testId="step-gestor-validation" />
          </div>
          <div className="border-t border-border pt-4 text-xs text-muted-foreground">
            {copy.cp_locked_footer || "Se tiver dúvidas ou não recebeu comunicação nas últimas 24h, contacte a sua Gestora."}
          </div>
        </div>
      </div>
    );
  }

  const beneficiaryNames = (data.beneficiaries || []).map(b => b.name).filter(Boolean);
  const bySection = {};
  (data.activities || []).forEach((a) => {
    const cat = a.category || "other";
    (bySection[cat] = bySection[cat] || []).push(a);
  });

  // Descarga do esboço inicial em .docx (view=client_summary).
  // Substitui o antigo PDF por um documento editável Word com paleta Caring
  // premium — objetivos globais definidos após a QT online, sem detalhe
  // operacional (que só entra na versão definitiva após visita presencial).
  const docxHref = `${process.env.REACT_APP_BACKEND_URL}/api/public/care-plan/${token}/docx`;

  return (
    <div className="min-h-screen bg-background" data-testid="public-care-plan">
      {/* 28/02/2026 — banner PROVISÓRIO removido do plano público.
          A distinção "Plano Preliminar" vs "PIC (validado)" é agora
          transmitida pelos títulos + selo de validação no corpo do
          documento; um banner amarelo introduziria alarme desnecessário
          para o cliente. */}
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-10">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--warning))] font-semibold mb-3" data-testid="public-care-plan-kicker">
          {data.is_provisional
            ? (copy.cp_hero_kicker_preliminar || "Plano Preliminar de Cuidados")
            : (copy.cp_hero_kicker_pic || "Plano Individual de Cuidados (PIC)")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-primary">
          {data.is_provisional
            ? (copy.cp_hero_title_preliminar || "Uma proposta de acompanhamento pensada em torno da pessoa, das suas rotinas e das suas necessidades.")
            : (copy.cp_hero_title || "O plano definitivo do seu acompanhamento.")}
        </h1>
        {beneficiaryNames.length > 0 && (
          <p className="mt-4 text-lg text-muted-foreground">
            {copy.cp_hero_subtitle_prefix || "Preparado para"} {beneficiaryNames.join(" e ")}.
          </p>
        )}

        {!data.is_provisional && data.approved_version && (
          <div className="mt-6 rounded-2xl bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 p-4 flex items-center gap-3" data-testid="approved-banner">
            <ShieldCheck className="w-5 h-5 text-[hsl(var(--success))]" />
            <div className="text-sm text-[hsl(var(--success))]">
              <b>{copy.cp_approved_body || "Documento validado pela nossa Direção Técnica."}</b> — versão <b>{data.approved_version}</b>.
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3 flex-wrap">
          <a href={docxHref} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 bg-primary hover:bg-primary text-primary-foreground rounded-full px-6 py-3 text-sm font-semibold"
             data-testid="care-plan-download">
            <Download className="w-4 h-4" /> {copy.cp_docx_button_label || "Descarregar Esboço (Word)"}
          </a>
        </div>
      </section>

      {/* Gestor Card */}
      {data.manager && (data.manager.name || data.manager.bio) && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
          <div className="bg-card border-l-4 border-primary rounded-2xl p-6 flex gap-5 flex-wrap" data-testid="manager-card">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[hsl(var(--info-bg))] border-2 border-primary flex items-center justify-center flex-shrink-0">
              {data.manager.photo_url ? (
                <img src={data.manager.photo_url} alt="Foto do gestor" className="w-full h-full object-cover" />
              ) : (
                <span className="font-display text-3xl text-primary">
                  {(data.manager.name || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{copy.cp_manager_kicker || "O SEU GESTOR DEDICADO"}</div>
              <div className="font-ui text-primary text-lg font-semibold">{data.manager.name || "Gestor"}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-[hsl(var(--warning))] font-semibold mt-1">
                {data.manager.title || copy.cp_manager_default_title || "Gestor de Cliente"}
              </div>
              {data.manager.bio && (
                <p className="text-sm text-foreground mt-3 leading-relaxed whitespace-pre-line">{data.manager.bio}</p>
              )}
              {(data.manager.email || data.manager.phone) && (
                <p className="text-xs text-muted-foreground mt-3">
                  {[data.manager.email, data.manager.phone].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Objectives */}
      {data.objectives?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10" data-testid="objectives-section">
          <h2 className="font-display text-3xl text-primary mb-4 inline-flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-[hsl(var(--warning))]" /> {copy.cp_objectives_title || "Objetivos do serviço"}
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {data.objectives.map((o, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 text-foreground" data-testid={`objective-${i}`}>
                <span className="text-primary font-bold mr-2">◆</span>{o}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Priorities */}
      {data.priorities?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <h2 className="font-display text-2xl text-primary mb-4">{copy.cp_priorities_title || "Prioridades identificadas"}</h2>
          <ul className="space-y-2">
            {data.priorities.map((p, i) => (
              <li key={i} className="text-foreground pl-6 relative">
                <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-[hsl(var(--warning))]"></span>
                {p}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Activities by category */}
      {Object.keys(bySection).length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16" data-testid="activities-section">
          <h2 className="font-display text-3xl text-primary mb-6 inline-flex items-center gap-3">
            <FileText className="w-6 h-6 text-[hsl(var(--warning))]" /> {copy.cp_activities_title || "Atividades diárias"}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-2xl">
            O que os nossos Ajudantes Familiares farão em cada visita, organizado por categoria.
          </p>
          <div className="space-y-6">
            {Object.entries(bySection).map(([cat, items]) => (
              <div key={cat} className="bg-card border border-border rounded-2xl overflow-hidden" data-testid={`activity-cat-${cat}`}>
                <div className="bg-primary text-primary-foreground px-5 py-3 font-ui text-sm uppercase tracking-[0.15em]">
                  {CATEGORY_LABELS[cat] || cat}
                </div>
                <div className="divide-y divide-[#EBF1F7]">
                  {items.map((a, i) => (
                    <div key={i} className="px-5 py-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                      <div className="md:col-span-2 text-foreground">{a.activity}</div>
                      <div className="text-xs text-muted-foreground">
                        <span className="uppercase tracking-[0.12em] text-primary font-semibold">Freq: </span>{a.frequency || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="uppercase tracking-[0.12em] text-primary font-semibold">Autonomia: </span>{a.autonomy_level || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 text-xs text-muted-foreground border-t border-border pt-6">
        <p>Este é o Plano de Cuidados provisório. Após a validação técnica,
        receberá a versão final. Para qualquer questão, contacte o seu gestor dedicado.</p>
      </footer>
    </div>
  );
}


function StatusStep({ done, pending, label, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]" : pending ? "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]" : "bg-muted text-muted-foreground"}`}>
        {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : pending ? <Clock className="w-3.5 h-3.5 animate-pulse" /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      </div>
      <div className={`text-sm ${done ? "text-primary" : "text-muted-foreground"}`}>{label}</div>
    </div>
  );
}
